import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CryptoJS from "crypto-js";
import InputBox from "../../components/InputBox";
import Button from "../../components/Button";
import { getMetadata } from "../../utils/encryptedStorage";
import { confirmGmailAccountSwitch, cancelGmailAccountSwitch } from "../../utils/gmailOAuth";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const SECRET_KEY = process.env.REACT_APP_SECRETKEY;

const CredentialsPage = ({ isSetupMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we're in setup mode (after company creation)
  const setupMode = isSetupMode || location.state?.setupMode || false;

  const [formData, setFormData] = useState({
    linkedin_username: "",
    linkedin_password: "",
    naukri_username: "",
    naukri_password: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [isEditMode, setIsEditMode] = useState(setupMode);
  const [hasExistingData, setHasExistingData] = useState(false);
  
  // Password visibility states
  const [showLinkedinPassword, setShowLinkedinPassword] = useState(false);
  const [showNaukriPassword, setShowNaukriPassword] = useState(false);

  // Gmail OAuth states
  const [gmailStatus, setGmailStatus] = useState({
    connected: false,
    gmail_email: null,
    gmail_connected_at: null,
    gmail_token_expiry: null,
    expiry_warning: false,
    expired: false,
    is_active: false,
  });
  const [gmailLoading, setGmailLoading] = useState(false);
  const [gmailError, setGmailError] = useState("");
  const [gmailSuccess, setGmailSuccess] = useState("");
  const [selectedMailProvider, setSelectedMailProvider] = useState("gmail");
  const [outlookStatus, setOutlookStatus] = useState({
    connected: false,
    outlook_email: null,
    outlook_connected_at: null,
    outlook_token_expiry: null,
    is_active: false,
  });
  const [outlookLoading, setOutlookLoading] = useState(false);
  const [outlookError, setOutlookError] = useState("");
  const [outlookSuccess, setOutlookSuccess] = useState("");
  const [noCompany, setNoCompany] = useState(false);

  // Pending account switch state
  const [pendingSwitch, setPendingSwitch] = useState(null); // { previousEmail, newEmail, switchWarning }
  const [switchActionLoading, setSwitchActionLoading] = useState(false);
  const gmailLockedByOutlook = Boolean(outlookStatus.connected && outlookStatus.is_active);
  const outlookLockedByGmail = Boolean(gmailStatus.connected && gmailStatus.is_active);

  // Handle confirm/cancel for pending account switch
  const handleConfirmSwitch = async () => {
    const metadata = getMetadata();
    const cid = metadata?.cid;
    if (!cid) return;
    setSwitchActionLoading(true);
    try {
      const result = await confirmGmailAccountSwitch(cid);
      setGmailSuccess(`Gmail switched to ${result.gmail_email || pendingSwitch?.newEmail || 'new account'} successfully!`);
      setPendingSwitch(null);
      fetchGmailStatus();
      setTimeout(() => setGmailSuccess(""), 5000);
    } catch (err) {
      setGmailError(err.message || "Failed to confirm account switch");
      setTimeout(() => setGmailError(""), 5000);
    } finally {
      setSwitchActionLoading(false);
    }
  };

  const handleCancelSwitch = async () => {
    const metadata = getMetadata();
    const cid = metadata?.cid;
    if (!cid) return;
    setSwitchActionLoading(true);
    try {
      await cancelGmailAccountSwitch(cid);
      setGmailSuccess(`Kept current Gmail account (${pendingSwitch?.previousEmail || gmailStatus.gmail_email || 'current'}).`);
      setPendingSwitch(null);
      setTimeout(() => setGmailSuccess(""), 5000);
    } catch (err) {
      setGmailError(err.message || "Failed to cancel account switch");
      setTimeout(() => setGmailError(""), 5000);
    } finally {
      setSwitchActionLoading(false);
    }
  };

  // Fetch Gmail status
  const fetchGmailStatus = useCallback(async () => {
    try {
      const metadata = getMetadata();
      const cid = metadata?.cid;
      if (!cid) {
        setNoCompany(true);
        return;
      }
      setNoCompany(false);

      const payload = { cid };
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();
      const response = await fetch(`${API_URL}/gmail/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: encrypted }),
      });
      const data = await response.json();
      if (response.ok && data.data) {
        const bytes = CryptoJS.AES.decrypt(data.data, SECRET_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        const statusData = JSON.parse(decrypted);
        setGmailStatus(statusData);
      }
    } catch (err) {
      console.error("Failed to fetch Gmail status:", err);
    }
  }, []);

  const fetchOutlookStatus = useCallback(async () => {
    try {
      const metadata = getMetadata();
      const cid = metadata?.cid;
      if (!cid) {
        setNoCompany(true);
        return;
      }
      setNoCompany(false);

      const payload = { cid };
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();
      const response = await fetch(`${API_URL}/outlook/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: encrypted }),
      });
      const data = await response.json();
      if (response.ok && data.data) {
        const bytes = CryptoJS.AES.decrypt(data.data, SECRET_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        const statusData = JSON.parse(decrypted);
        setOutlookStatus(statusData);
        if (statusData?.is_active) {
          setSelectedMailProvider("outlook");
        }
      }
    } catch (err) {
      console.error("Failed to fetch Outlook status:", err);
    }
  }, []);

  // Listen for mail OAuth popup callback messages
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'GMAIL_CONNECTED') {
        if (event.data.success) {
          setGmailSuccess(`Gmail connected successfully${event.data.gmail_email ? ` (${event.data.gmail_email})` : ''}!`);
          setGmailError("");
          fetchGmailStatus();
          fetchOutlookStatus();
          setTimeout(() => setGmailSuccess(""), 5000);
        } else {
          setGmailError(event.data.error || "Failed to connect Gmail");
          setTimeout(() => setGmailError(""), 5000);
        }
        setGmailLoading(false);
      } else if (event.data?.type === 'GMAIL_SWITCH_PENDING') {
        // Account switch needs confirmation — show inline confirmation UI
        setPendingSwitch({
          previousEmail: event.data.previous_email,
          newEmail: event.data.new_email,
          switchWarning: event.data.switch_warning,
        });
        setGmailLoading(false);
      } else if (event.data?.type === 'OUTLOOK_CONNECTED') {
        if (event.data.success) {
          setOutlookSuccess(`Outlook connected successfully${event.data.outlook_email ? ` (${event.data.outlook_email})` : ''}!`);
          setOutlookError("");
          fetchGmailStatus();
          fetchOutlookStatus();
          setTimeout(() => setOutlookSuccess(""), 5000);
        } else {
          setOutlookError(event.data.error || "Failed to connect Outlook");
          setTimeout(() => setOutlookError(""), 5000);
        }
        setOutlookLoading(false);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [fetchGmailStatus, fetchOutlookStatus]);

  // Handle Connect Gmail button click
  const handleConnectGmail = async () => {
    const metadata = getMetadata();
    const cid = metadata?.cid;
    if (!cid) {
      setGmailError("Please create a company first before connecting Gmail.");
      setNoCompany(true);
      return;
    }

    setGmailLoading(true);
    setGmailError("");
    setGmailSuccess("");

    try {
      const callbackUrl = `${window.location.origin}/gmail-callback`;
      const userId = metadata?.uid || metadata?.userId || metadata?.id || null;
      const payload = { cid, user_id: userId, callback_url: callbackUrl };
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();

      const response = await fetch(`${API_URL}/gmail/auth-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: encrypted }),
      });

      const data = await response.json();

      if (response.ok && data.data) {
        const bytes = CryptoJS.AES.decrypt(data.data, SECRET_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        const result = JSON.parse(decrypted);

        if (result.auth_url) {
          // Open Google OAuth in a popup window
          const width = 600;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;
          const popup = window.open(
            result.auth_url,
            'Gmail OAuth',
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
          );

          // Check if popup was blocked
          if (!popup || popup.closed) {
            setGmailError("Popup blocked. Please allow popups for this site and try again.");
            setGmailLoading(false);
          }
          // Loading state will be cleared by the message event listener
        } else {
          setGmailError("Failed to get OAuth URL");
          setGmailLoading(false);
        }
      } else {
        const errorMsg = data.error || "Failed to start Gmail connection";
        setGmailError(errorMsg);
        setGmailLoading(false);
      }
    } catch (err) {
      console.error("Connect Gmail error:", err);
      setGmailError("Failed to connect Gmail. Please try again.");
      setGmailLoading(false);
    }
  };

  // Handle Disconnect Gmail
  const handleDisconnectGmail = async () => {
    const metadata = getMetadata();
    const cid = metadata?.cid;
    if (!cid) return;

    setGmailLoading(true);
    setGmailError("");
    try {
      const payload = { cid };
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();
      const response = await fetch(`${API_URL}/gmail/disconnect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: encrypted }),
      });

      if (response.ok) {
        setGmailStatus({
          connected: false,
          gmail_email: null,
          gmail_connected_at: null,
          gmail_token_expiry: null,
          expiry_warning: false,
          expired: false,
          is_active: false,
        });
        setGmailSuccess("Gmail disconnected successfully.");
        setTimeout(() => setGmailSuccess(""), 3000);
      } else {
        const data = await response.json();
        setGmailError(data.error || "Failed to disconnect Gmail");
      }
    } catch (err) {
      setGmailError("Failed to disconnect Gmail");
    } finally {
      setGmailLoading(false);
    }
  };

  const handleConnectOutlook = async () => {
    const metadata = getMetadata();
    const cid = metadata?.cid;
    if (!cid) {
      setOutlookError("Please create a company first before connecting Outlook.");
      setNoCompany(true);
      return;
    }

    setOutlookLoading(true);
    setOutlookError("");
    setOutlookSuccess("");

    try {
      const callbackUrl = `${window.location.origin}/outlook-callback`;
      const userId = metadata?.uid || metadata?.userId || metadata?.id || null;
      const payload = { cid, user_id: userId, callback_url: callbackUrl };
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();

      const response = await fetch(`${API_URL}/outlook/auth-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: encrypted }),
      });

      const data = await response.json();

      if (response.ok && data.data) {
        const bytes = CryptoJS.AES.decrypt(data.data, SECRET_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        const result = JSON.parse(decrypted);

        if (result.auth_url) {
          const width = 600;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;
          const popup = window.open(
            result.auth_url,
            'Outlook OAuth',
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
          );

          if (!popup || popup.closed) {
            setOutlookError("Popup blocked. Please allow popups for this site and try again.");
            setOutlookLoading(false);
          }
        } else {
          setOutlookError("Failed to get OAuth URL");
          setOutlookLoading(false);
        }
      } else {
        setOutlookError(data.error || "Failed to start Outlook connection");
        setOutlookLoading(false);
      }
    } catch (err) {
      console.error("Connect Outlook error:", err);
      setOutlookError("Failed to connect Outlook. Please try again.");
      setOutlookLoading(false);
    }
  };

  const handleDisconnectOutlook = async () => {
    const metadata = getMetadata();
    const cid = metadata?.cid;
    if (!cid) return;

    setOutlookLoading(true);
    setOutlookError("");
    try {
      const payload = { cid };
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();
      const response = await fetch(`${API_URL}/outlook/disconnect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: encrypted }),
      });

      if (response.ok) {
        setOutlookStatus({
          connected: false,
          outlook_email: null,
          outlook_connected_at: null,
          outlook_token_expiry: null,
          is_active: false,
        });
        setOutlookSuccess("Outlook disconnected successfully.");
        setTimeout(() => setOutlookSuccess(""), 3000);
      } else {
        const data = await response.json();
        setOutlookError(data.error || "Failed to disconnect Outlook");
      }
    } catch (err) {
      setOutlookError("Failed to disconnect Outlook");
    } finally {
      setOutlookLoading(false);
    }
  };

  // Fetch existing credentials on mount (always — needed to detect create vs update)
  useEffect(() => {
    fetchCredentials();
    fetchGmailStatus();
    fetchOutlookStatus();
  }, [fetchGmailStatus, fetchOutlookStatus]);

  const fetchCredentials = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const SECRET_KEY = process.env.REACT_APP_SECRETKEY;

      // Get company ID from metadata
      const metadata = getMetadata();
      const cid = metadata?.cid;

      if (!cid) {
        setErrors({ submit: "Company not found. Please set up your company first." });
        setIsFetching(false);
        return;
      }

      const payload = { cid };
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();

      const response = await fetch(`${API_URL}/credentials/get`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ data: encrypted }),
      });

      const data = await response.json();

      if (response.ok && data.data) {
        const bytes = CryptoJS.AES.decrypt(data.data, SECRET_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        const credentials = JSON.parse(decrypted);

        if (credentials && Object.keys(credentials).length > 0 && credentials.id) {
          setFormData({
            linkedin_username: credentials.linkedin_username || "",
            linkedin_password: "", // Don't show hashed passwords
            naukri_username: credentials.naukri_username || "",
            naukri_password: "", // Don't show hashed passwords
          });
          setHasExistingData(true);
          setIsEditMode(setupMode ? true : false); // Stay in edit mode during setup
        } else {
          // No existing data - enable edit mode
          setIsEditMode(true);
        }
      } else {
        // No data returned - enable edit mode
        setIsEditMode(true);
      }
    } catch (err) {
      console.error("Failed to fetch credentials:", err);
      // Enable edit mode on error so user can still add credentials
      setIsEditMode(true);
    } finally {
      setIsFetching(false);
    }
  };

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSkip = () => {
    navigate("/dashboard");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    try {
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const SECRET_KEY = process.env.REACT_APP_SECRETKEY;

      if (!SECRET_KEY) {
        setErrors({ submit: "Missing encryption key configuration." });
        setIsLoading(false);
        return;
      }

      // Get company ID from metadata
      const metadata = getMetadata();
      const cid = metadata?.cid;

      if (!cid) {
        setErrors({ submit: "Company not found. Please set up your company first." });
        setIsLoading(false);
        return;
      }

      // Build payload
      const payload = {
        cid,
        linkedin_username: formData.linkedin_username,
        linkedin_password: formData.linkedin_password,
        naukri_username: formData.naukri_username,
        naukri_password: formData.naukri_password,
      };

      // Encrypt payload
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();

      const endpoint = hasExistingData ? "update" : "create";
      const method = hasExistingData ? "PUT" : "POST";

      const response = await fetch(`${API_URL}/credentials/${endpoint}`, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ data: encrypted }),
      });

      const data = await response.json();

      if (response.ok) {
        setShowSuccess(true);
        setIsEditMode(false);
        setHasExistingData(true);

        // Show success message briefly, then reset
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
      } else {
        let errorMessage = data.error || "Failed to save credentials";
        // If create failed because credentials exist, retry with update
        if (errorMessage.toLowerCase().includes('already exist') && !hasExistingData) {
          setHasExistingData(true);
          // Auto-retry with update endpoint
          try {
            const retryResponse = await fetch(`${API_URL}/credentials/update`, {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ data: encrypted }),
            });
            const retryData = await retryResponse.json();
            if (retryResponse.ok) {
              setShowSuccess(true);
              setIsEditMode(false);
              setTimeout(() => setShowSuccess(false), 3000);
              return;
            }
            errorMessage = retryData.error || "Failed to update credentials";
          } catch (_) {}
        }
        setErrors({ submit: errorMessage });
      }
    } catch (error) {
      console.error("Credentials save error:", error);
      setErrors({
        submit: "An error occurred while saving credentials. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50 dark:bg-gray-950 transition-all duration-250">
      <div className="w-full h-full bg-white dark:bg-gray-900 px-4 sm:px-6 lg:px-8 py-4 sm:py-5 lg:py-6 space-y-8 lg:space-y-10">
        {/* Header */}
        <div className="w-full relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600" style={{ height: "80px" }}>
          <div className="relative z-10 px-6 sm:px-8 lg:px-8 py-6">
            <h1 className="text-xl sm:text-2xl font-semibold text-white">
              {setupMode ? "Platform Credentials Setup" : "Platform Credentials"}
            </h1>
          </div>
        </div>

        {/* Setup mode description */}
        {setupMode && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <p className="text-blue-800 dark:text-blue-200 text-sm">
              Add your LinkedIn and Naukri credentials to enable automated job posting and candidate sourcing. 
              Your passwords are securely encrypted before storage.
              Connect your Gmail to enable sending emails directly from your account.
            </p>
          </div>
        )}

        {/* Mail Provider Connection Section */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4Z" fill="#EA4335" fillOpacity="0.1" stroke="#EA4335" strokeWidth="1.5" />
              <path d="M22 6L12 13L2 6" stroke="#EA4335" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Mail Provider Connection</h2>
          </div>

          {/* No company warning */}
          {noCompany && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <p className="text-amber-800 dark:text-amber-200 text-sm font-medium">Company Required</p>
                <p className="text-amber-700 dark:text-amber-300 text-xs mt-1">
                  Please create a company first before connecting Gmail.{" "}
                  <button
                    type="button"
                    onClick={() => navigate(setupMode ? "/auth/workspace-setup" : "/account/business-details")}
                    className="text-amber-900 dark:text-amber-100 underline font-medium"
                  >
                    Create Company
                  </button>
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => !gmailLockedByOutlook && setSelectedMailProvider("gmail")}
              disabled={gmailLockedByOutlook}
              className={`text-left rounded-xl border-2 p-4 transition-all ${
                selectedMailProvider === "gmail"
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                  : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300"
              } ${gmailLockedByOutlook ? "opacity-50 cursor-not-allowed hover:border-gray-200 dark:hover:border-gray-700" : ""}`}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Gmail</p>
                {gmailLockedByOutlook ? (
                  <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">Disabled</span>
                ) : gmailStatus.connected && (
                  <span className="text-[11px] font-semibold text-green-600 dark:text-green-400">Connected</span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Use Google Gmail, Calendar, and Meet permissions.
              </p>
            </button>

            <button
              type="button"
              onClick={() => !outlookLockedByGmail && setSelectedMailProvider("outlook")}
              disabled={outlookLockedByGmail}
              className={`text-left rounded-xl border-2 p-4 transition-all ${
                selectedMailProvider === "outlook"
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                  : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300"
              } ${outlookLockedByGmail ? "opacity-50 cursor-not-allowed hover:border-gray-200 dark:hover:border-gray-700" : ""}`}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Outlook</p>
                {outlookLockedByGmail ? (
                  <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">Disabled</span>
                ) : outlookStatus.connected && (
                  <span className="text-[11px] font-semibold text-green-600 dark:text-green-400">Connected</span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Use Microsoft Outlook and Microsoft Graph permissions.
              </p>
            </button>
          </div>

          {/* Token expiry warning (1 day before) */}
          {selectedMailProvider === "gmail" && gmailStatus.connected && gmailStatus.expiry_warning && !gmailStatus.expired && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-4 flex items-start gap-3">
              <svg className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-orange-800 dark:text-orange-200 text-sm font-medium">Gmail Token Expiring Soon</p>
                <p className="text-orange-700 dark:text-orange-300 text-xs mt-1">
                  Your Gmail connection will expire soon. Please re-connect to prevent email sending failures.
                </p>
              </div>
            </div>
          )}

          {/* Token expired warning */}
          {selectedMailProvider === "gmail" && gmailStatus.connected && gmailStatus.expired && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <p className="text-red-800 dark:text-red-200 text-sm font-medium">Gmail Token Expired</p>
                <p className="text-red-700 dark:text-red-300 text-xs mt-1">
                  Your Gmail connection has expired. Please re-connect Gmail to continue sending emails.
                </p>
              </div>
            </div>
          )}

          {/* Gmail Error */}
          {selectedMailProvider === "gmail" && gmailError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3">
              <p className="text-red-600 dark:text-red-400 text-sm">{gmailError}</p>
            </div>
          )}

          {/* Gmail Success */}
          {selectedMailProvider === "gmail" && gmailSuccess && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-3">
              <p className="text-green-600 dark:text-green-400 text-sm">{gmailSuccess}</p>
            </div>
          )}

          {/* Pending account switch confirmation */}
          {selectedMailProvider === "gmail" && pendingSwitch && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-xl p-5 space-y-4">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="text-amber-900 dark:text-amber-100 font-semibold text-sm">Confirm Gmail Account Switch</p>
              </div>

              {/* Current vs New account comparison */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 ml-9">
                {/* Current account */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">Current Account</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{pendingSwitch.previousEmail}</p>
                      {gmailStatus.gmail_connected_at && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                          Connected {new Date(gmailStatus.gmail_connected_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* New account */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                  <p className="text-[11px] font-semibold text-blue-500 dark:text-blue-400 uppercase tracking-wider mb-1.5">New Account</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{pendingSwitch.newEmail}</p>
                      <p className="text-[11px] text-blue-600 dark:text-blue-400">Pending confirmation</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Warning */}
              <div className="ml-9 bg-amber-100/60 dark:bg-amber-900/30 rounded-lg px-3 py-2">
                <p className="text-amber-800 dark:text-amber-200 text-xs leading-relaxed">
                  <strong>What happens if you switch:</strong> Past email conversations sent from {pendingSwitch.previousEmail} will no longer sync.
                  All new outreach emails will be sent from {pendingSwitch.newEmail}.
                </p>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3 ml-9">
                <button
                  type="button"
                  onClick={handleConfirmSwitch}
                  disabled={switchActionLoading}
                  className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors"
                >
                  {switchActionLoading ? 'Switching...' : 'Confirm Switch'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelSwitch}
                  disabled={switchActionLoading}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg disabled:opacity-50 transition-colors"
                >
                  Keep Current Account
                </button>
              </div>
            </div>
          )}

          {selectedMailProvider === "outlook" && outlookError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3">
              <p className="text-red-600 dark:text-red-400 text-sm">{outlookError}</p>
            </div>
          )}

          {selectedMailProvider === "outlook" && outlookSuccess && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-3">
              <p className="text-green-600 dark:text-green-400 text-sm">{outlookSuccess}</p>
            </div>
          )}

          {selectedMailProvider === "outlook" && (
            outlookStatus.connected ? (
              <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Connected to {outlookStatus.outlook_email || "Outlook"}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {outlookStatus.outlook_connected_at
                        ? `Connected on ${new Date(outlookStatus.outlook_connected_at).toLocaleString()}`
                        : "Outlook connected"}
                      {outlookStatus.is_active ? " · Active sending provider" : ""}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleConnectOutlook}
                    disabled={outlookLoading || noCompany || outlookLockedByGmail}
                    className="px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-300 dark:border-blue-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 transition-colors"
                  >
                    Reconnect
                  </button>
                  <button
                    type="button"
                    onClick={handleDisconnectOutlook}
                    disabled={outlookLoading}
                    className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4 py-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center max-w-md">
                  Connect your Outlook account to send emails and use Microsoft calendar permissions.
                </p>
                <button
                  type="button"
                  onClick={handleConnectOutlook}
                  disabled={outlookLoading || noCompany || outlookLockedByGmail}
                  className="flex items-center gap-2 px-6 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 hover:border-blue-400 hover:shadow-md dark:hover:border-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {outlookLoading ? (
                    <svg className="animate-spin h-5 w-5 text-gray-500" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <span className="w-5 h-5 rounded bg-blue-600 text-white flex items-center justify-center text-xs font-bold">O</span>
                  )}
                  {outlookLoading ? "Connecting..." : "Connect Outlook"}
                </button>
              </div>
            )
          )}

          {/* Gmail connection status & actions */}
          {selectedMailProvider === "gmail" && (gmailStatus.connected && !gmailStatus.expired ? (
            <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Connected to {gmailStatus.gmail_email || "Gmail"}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {gmailStatus.gmail_connected_at
                      ? `Connected on ${new Date(gmailStatus.gmail_connected_at).toLocaleString()}`
                      : "Gmail connected"}
                    {gmailStatus.is_active ? " · Active sending provider" : " · Token refreshes automatically"}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleConnectGmail}
                  disabled={gmailLoading || noCompany || gmailLockedByOutlook}
                  className="px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-300 dark:border-blue-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 transition-colors"
                >
                  Reconnect
                </button>
                <button
                  type="button"
                  onClick={handleDisconnectGmail}
                  disabled={gmailLoading}
                  className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
                >
                  Disconnect
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 py-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center max-w-md">
                {gmailStatus.expired
                  ? "Your Gmail token has expired. Click below to re-authorize and continue sending emails."
                  : "Connect your Gmail account to send emails directly from your email address. This uses secure Google OAuth — we never see your password."}
              </p>
              <button
                type="button"
                onClick={handleConnectGmail}
                disabled={gmailLoading || noCompany || gmailLockedByOutlook}
                className="flex items-center gap-2 px-6 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 hover:border-blue-400 hover:shadow-md dark:hover:border-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {gmailLoading ? (
                  <svg className="animate-spin h-5 w-5 text-gray-500" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                    <path d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6Z" fill="#EA4335" fillOpacity="0.1"/>
                    <path d="M22 6L12 13L2 6" stroke="#EA4335" strokeWidth="1.5" strokeLinecap="round"/>
                    <rect x="2" y="4" width="20" height="16" rx="2" stroke="#EA4335" strokeWidth="1.5"/>
                  </svg>
                )}
                {gmailLoading
                  ? "Connecting..."
                  : gmailStatus.expired
                    ? "Re-connect Gmail"
                    : "Connect Gmail"}
              </button>
            </div>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* LinkedIn Section */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-6 space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">LinkedIn Credentials</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  LinkedIn Username/Email
                </label>
                <InputBox
                  type="text"
                  value={formData.linkedin_username}
                  onChange={(e) => handleInputChange("linkedin_username", e.target.value)}
                  placeholder="Enter LinkedIn email"
                  disabled={!isEditMode}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  LinkedIn Password
                </label>
                <div className="relative">
                  <InputBox
                    type={showLinkedinPassword ? "text" : "password"}
                    value={formData.linkedin_password}
                    onChange={(e) => handleInputChange("linkedin_password", e.target.value)}
                    placeholder={hasExistingData && !isEditMode ? "••••••••" : "Enter LinkedIn password"}
                    disabled={!isEditMode}
                  />
                  {isEditMode && (
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      onClick={() => setShowLinkedinPassword(!showLinkedinPassword)}
                    >
                      {showLinkedinPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Naukri Section */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-6 space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-6 h-6 text-orange-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Naukri Credentials</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Naukri Username/Email
                </label>
                <InputBox
                  type="text"
                  value={formData.naukri_username}
                  onChange={(e) => handleInputChange("naukri_username", e.target.value)}
                  placeholder="Enter Naukri email"
                  disabled={!isEditMode}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Naukri Password
                </label>
                <div className="relative">
                  <InputBox
                    type={showNaukriPassword ? "text" : "password"}
                    value={formData.naukri_password}
                    onChange={(e) => handleInputChange("naukri_password", e.target.value)}
                    placeholder={hasExistingData && !isEditMode ? "••••••••" : "Enter Naukri password"}
                    disabled={!isEditMode}
                  />
                  {isEditMode && (
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      onClick={() => setShowNaukriPassword(!showNaukriPassword)}
                    >
                      {showNaukriPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {errors.submit && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
              <p className="text-red-600 dark:text-red-400 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Success Display */}
          {showSuccess && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
              <p className="text-green-600 dark:text-green-400 text-sm">
                Credentials saved successfully!
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-4">
            {setupMode && (
              <Button
                type="button"
                variant="secondary"
                onClick={handleSkip}
                disabled={isLoading}
              >
                Skip for now
              </Button>
            )}
            
            {!isEditMode && hasExistingData && !setupMode && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsEditMode(true)}
              >
                Edit Credentials
              </Button>
            )}
            
            {isEditMode && (
              <>
                {!setupMode && hasExistingData && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setIsEditMode(false);
                      fetchCredentials();
                    }}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                )}
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    hasExistingData ? "Update Credentials" : "Save Credentials"
                  )}
                </Button>
              </>
            )}
          </div>
        </form>

        {/* Security Note */}
        <div className="bg-gray-50 dark:bg-gray-800/30 rounded-xl p-4 mt-6">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Your credentials are encrypted before being stored in our database. We use industry-standard 
              encryption to protect your sensitive information.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CredentialsPage;
